import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { GRUPOS_HORAS, TIPOS_HORAS, TIPOS_NOMINA, ESTADOS } from './nomina';

// Recargos y extras: columnas del detalle por día.
const TIPOS_DIA = TIPOS_HORAS.filter((t) => t.grupo === 'Recargos' || t.grupo === 'Extras');

const FMT_PESOS = '"$"#,##0';
const FMT_HORAS = '#,##0.00';
const ENCABEZADO = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E79' } };

const estilarEncabezado = (fila) => {
  fila.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  fila.eachCell((c) => {
    c.fill = ENCABEZADO;
    c.alignment = { vertical: 'middle', wrapText: true };
  });
  fila.height = 30;
};

/**
 * Excel de la nómina: hoja "Resumen" (una fila por persona con horas y valores por
 * tipo, conceptos y neto) y hoja "Detalle por día" (cada marcación o ausencia).
 */
export const exportarNominaExcel = async (nomina) => {
  if (!nomina) return;
  const lineas = [...(nomina.lineas || [])].sort((a, b) =>
    String(a.empleado?.nombre).localeCompare(String(b.empleado?.nombre)),
  );
  const conceptos = [];
  for (const l of lineas) {
    for (const c of l.conceptos || []) {
      if (!conceptos.some((x) => x.nombre === c.nombre)) conceptos.push({ nombre: c.nombre, naturaleza: c.naturaleza });
    }
  }

  const wb = new ExcelJS.Workbook();
  wb.creator = 'ERP Proflux';
  wb.created = new Date();

  // ------------------------------------------------------------ Resumen
  const ws = wb.addWorksheet('Resumen', { views: [{ state: 'frozen', xSplit: 2, ySplit: 6 }] });
  ws.addRow([`Nómina ${nomina.nombre}`]).font = { bold: true, size: 14 };
  ws.addRow([
    `Periodo: ${nomina.desde} a ${nomina.hasta}`,
    '',
    `Tipo: ${TIPOS_NOMINA[nomina.tipo] || nomina.tipo}`,
    '',
    `Estado: ${ESTADOS[nomina.estado]?.label || nomina.estado}`,
  ]);
  ws.addRow([
    `Sedes: ${(nomina.centros || []).length ? nomina.centros.map((c) => c.nombre).join(', ') : 'Todas'}`,
  ]);
  ws.addRow([
    `Divisor hora: ${nomina.parametros?.divisorHora ?? ''}`,
    '',
    `Incapacidad: ${nomina.parametros?.porcentajeIncapacidad ?? ''} %`,
  ]);
  ws.addRow([]);

  const columnas = [
    { titulo: 'Persona', ancho: 32 },
    { titulo: 'Cédula', ancho: 14 },
    { titulo: 'Cargo', ancho: 20 },
    { titulo: 'Tipo nómina', ancho: 12 },
    { titulo: 'Sedes', ancho: 26 },
    { titulo: 'Salario', ancho: 13, fmt: FMT_PESOS },
    { titulo: 'Valor hora', ancho: 11, fmt: FMT_PESOS },
    { titulo: 'Días laborados', ancho: 9, fmt: FMT_HORAS },
    { titulo: 'Horas base', ancho: 9, fmt: FMT_HORAS },
    { titulo: 'Horas trabajadas', ancho: 10, fmt: FMT_HORAS },
    ...TIPOS_HORAS.map((t) => ({ titulo: `H. ${t.largo}`, ancho: 11, fmt: FMT_HORAS })),
    ...TIPOS_HORAS.map((t) => ({ titulo: `$ ${t.largo}`, ancho: 13, fmt: FMT_PESOS })),
    { titulo: '$ Auxilio de transporte', ancho: 13, fmt: FMT_PESOS },
    { titulo: '$ Total recargos', ancho: 13, fmt: FMT_PESOS },
    { titulo: '$ Total extras', ancho: 13, fmt: FMT_PESOS },
    ...conceptos.map((c) => ({ titulo: `${c.naturaleza === 'deduccion' ? '(-) ' : '(+) '}${c.nombre}`, ancho: 13, fmt: FMT_PESOS })),
    { titulo: 'Ajustes (+)', ancho: 12, fmt: FMT_PESOS },
    { titulo: 'Ajustes (-)', ancho: 12, fmt: FMT_PESOS },
    { titulo: 'Total devengado', ancho: 14, fmt: FMT_PESOS },
    { titulo: 'Total deducciones', ancho: 14, fmt: FMT_PESOS },
    { titulo: 'Neto a pagar', ancho: 14, fmt: FMT_PESOS },
    { titulo: 'Aportes empleador', ancho: 14, fmt: FMT_PESOS },
    { titulo: 'Prestaciones (provisión)', ancho: 14, fmt: FMT_PESOS },
    { titulo: 'Costo total empresa', ancho: 15, fmt: FMT_PESOS },
    { titulo: 'Pendientes', ancho: 10 },
    { titulo: 'Advertencias', ancho: 50 },
  ];
  estilarEncabezado(ws.addRow(columnas.map((c) => c.titulo)));
  columnas.forEach((c, i) => {
    ws.getColumn(i + 1).width = c.ancho;
    if (c.fmt) ws.getColumn(i + 1).numFmt = c.fmt;
  });

  for (const l of lineas) {
    const ajustes = l.ajustes || [];
    const suma = (nat) =>
      ajustes.filter((a) => (a.naturaleza === 'deduccion') === (nat === 'deduccion')).reduce((s, a) => s + (Number(a.valor) || 0), 0);
    ws.addRow([
      l.empleado?.nombre,
      l.empleado?.cedula || '',
      l.empleado?.rol || '',
      l.empleado?.tipoNomina || '',
      (l.centros || []).map((c) => c.nombre).join(', '),
      l.salario,
      l.valorHora,
      l.diasLaborados || 0,
      l.horasBase,
      l.horas?.trabajadas || 0,
      ...TIPOS_HORAS.map((t) => l.horas?.[t.key] || 0),
      ...TIPOS_HORAS.map((t) => l.valores?.[t.key] || 0),
      l.valores?.auxilioTransporte || 0,
      l.valores?.recargos || 0,
      l.valores?.extras || 0,
      ...conceptos.map((c) => (l.conceptos || []).find((x) => x.nombre === c.nombre)?.montoCalculado || 0),
      suma('devengado'),
      suma('deduccion'),
      l.totalDevengado,
      l.totalDeducciones,
      l.neto,
      l.costoEmpresa?.aportes?.total || 0,
      l.costoEmpresa?.prestaciones?.total || 0,
      l.costoEmpresa?.total || 0,
      l.pendientesAutorizar || 0,
      (l.advertencias || []).join(' | '),
    ]);
  }
  const filaTotal = ws.addRow([
    'TOTAL',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    ...[
      'trabajadas',
      ...TIPOS_HORAS.map((t) => t.key),
    ].map((k) => lineas.reduce((s, l) => s + (Number(l.horas?.[k]) || 0), 0)),
    ...[...TIPOS_HORAS.map((t) => t.key), 'auxilioTransporte', 'recargos', 'extras'].map((k) =>
      lineas.reduce((s, l) => s + (Number(l.valores?.[k]) || 0), 0),
    ),
    ...conceptos.map((c) =>
      lineas.reduce((s, l) => s + ((l.conceptos || []).find((x) => x.nombre === c.nombre)?.montoCalculado || 0), 0),
    ),
    '',
    '',
    lineas.reduce((s, l) => s + (l.totalDevengado || 0), 0),
    lineas.reduce((s, l) => s + (l.totalDeducciones || 0), 0),
    lineas.reduce((s, l) => s + (l.neto || 0), 0),
    lineas.reduce((s, l) => s + (l.costoEmpresa?.aportes?.total || 0), 0),
    lineas.reduce((s, l) => s + (l.costoEmpresa?.prestaciones?.total || 0), 0),
    lineas.reduce((s, l) => s + (l.costoEmpresa?.total || 0), 0),
  ]);
  filaTotal.font = { bold: true };

  // ------------------------------------------------------------ Detalle por día
  const wd = wb.addWorksheet('Detalle por día', { views: [{ state: 'frozen', ySplit: 1 }] });
  const colsDia = [
    ['Persona', 32],
    ['Cédula', 14],
    ['Fecha', 12],
    ['Tipo de día', 12],
    ['Sede', 24],
    ['Entrada', 9],
    ['Salida', 9],
    ['Horas', 9],
    ['Diurnas', 9],
    ['Nocturnas', 9],
    ['Ordinarias', 10],
    ['Extras', 9],
    ...TIPOS_DIA.map((t) => [t.largo, 12]),
    ['Estado', 18],
    ['Ausencia', 30],
  ];
  estilarEncabezado(wd.addRow(colsDia.map((c) => c[0])));
  colsDia.forEach(([, ancho], i) => {
    wd.getColumn(i + 1).width = ancho;
  });
  for (let i = 8; i <= 12 + TIPOS_DIA.length; i++) wd.getColumn(i).numFmt = FMT_HORAS;
  for (const l of lineas) {
    for (const d of l.dias || []) {
      const ausencia = d.ausencia
        ? `${d.ausencia.tipo === 'incapacidad' ? 'Incapacidad' : 'Permiso'}${
            d.ausencia.tipo === 'permiso' && !d.ausencia.remunerado ? ' no remunerado' : ''
          }${d.ausencia.motivo ? ` · ${d.ausencia.motivo}` : ''}`
        : '';
      wd.addRow([
        l.empleado?.nombre,
        l.empleado?.cedula || '',
        d.fecha,
        d.tipoDia,
        d.centro || '',
        d.entrada || '',
        d.salida || '',
        d.horas,
        d.diurnas,
        d.nocturnas,
        d.ordinarias,
        d.extras,
        ...TIPOS_DIA.map((t) => d.detalle?.[t.key] || 0),
        d.ausencia ? '' : d.repetida ? 'Marcación repetida' : d.sinSalida ? 'Sin salida' : d.pendiente ? 'Pendiente de autorizar' : 'Autorizado',
        ausencia,
      ]);
    }
  }

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `nomina_${nomina.nombre}.xlsx`,
  );
};

/**
 * Excel del listado de nóminas por periodo: una fila por nómina con horas, valor hora
 * promedio, recargos, extras y totales, más una fila de totales.
 */
export const exportarResumenNominas = async (nominas, { filtro } = {}) => {
  const lista = [...(nominas || [])].sort((a, b) => String(a.desde).localeCompare(String(b.desde)));
  const suma = (obj, grupo) =>
    (GRUPOS_HORAS.find((g) => g.grupo === grupo)?.tipos || []).reduce((s, t) => s + (Number(obj?.[t.key]) || 0), 0);

  const wb = new ExcelJS.Workbook();
  wb.creator = 'ERP Proflux';
  wb.created = new Date();
  const ws = wb.addWorksheet('Nóminas por periodo', { views: [{ state: 'frozen', xSplit: 1, ySplit: 3 }] });
  ws.addRow(['Nóminas por periodo']).font = { bold: true, size: 14 };
  ws.addRow([`Estado: ${filtro && filtro !== 'todas' ? ESTADOS[filtro]?.label || filtro : 'Todas'} · Generado ${new Date().toLocaleString('es-CO')}`]);

  const columnas = [
    { titulo: 'Nombre', ancho: 30 },
    { titulo: 'Desde', ancho: 11 },
    { titulo: 'Hasta', ancho: 11 },
    { titulo: 'Tipo', ancho: 11 },
    { titulo: 'Estado', ancho: 11 },
    { titulo: 'Personas', ancho: 9, fmt: '#,##0' },
    { titulo: 'Horas trabajadas', ancho: 11, fmt: FMT_HORAS },
    { titulo: 'Horas base', ancho: 9, fmt: FMT_HORAS },
    { titulo: 'Valor hora promedio', ancho: 12, fmt: FMT_PESOS },
    { titulo: 'Horas con recargo', ancho: 11, fmt: FMT_HORAS },
    { titulo: '$ Recargos', ancho: 14, fmt: FMT_PESOS },
    { titulo: 'Horas extra', ancho: 10, fmt: FMT_HORAS },
    { titulo: '$ Extras', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Básico', ancho: 15, fmt: FMT_PESOS },
    { titulo: '$ Auxilio de transporte', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Devengado', ancho: 15, fmt: FMT_PESOS },
    { titulo: '$ Deducciones', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Neto', ancho: 15, fmt: FMT_PESOS },
    { titulo: '$ Aportes empleador', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Prestaciones', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Costo total empresa', ancho: 15, fmt: FMT_PESOS },
    { titulo: 'Pendientes de autorizar', ancho: 11, fmt: '#,##0' },
  ];
  estilarEncabezado(ws.addRow(columnas.map((c) => c.titulo)));
  columnas.forEach((c, i) => {
    ws.getColumn(i + 1).width = c.ancho;
    if (c.fmt) ws.getColumn(i + 1).numFmt = c.fmt;
  });

  const valores = (n) => {
    const t = n.totales || {};
    return [
      t.personas || 0,
      Number(t.horas?.trabajadas) || 0,
      Number(n.horasBase) || 0,
      Number(n.valorHoraPromedio) || 0,
      suma(t.horas, 'Recargos'),
      Number(t.valores?.recargos) || 0,
      suma(t.horas, 'Extras'),
      Number(t.valores?.extras) || 0,
      Number(t.valores?.basico ?? t.valores?.ordinarias) || 0,
      Number(t.valores?.auxilioTransporte) || 0,
      Number(t.devengado) || 0,
      Number(t.deducciones) || 0,
      Number(t.neto) || 0,
      Number(t.costoEmpresa?.aportes?.total) || 0,
      Number(t.costoEmpresa?.prestaciones?.total) || 0,
      Number(t.costoEmpresa?.total) || 0,
      Number(n.pendientesAutorizar) || 0,
    ];
  };
  const filas = lista.map((n) => {
    const v = valores(n);
    ws.addRow([n.nombre, n.desde, n.hasta, TIPOS_NOMINA[n.tipo] || n.tipo, ESTADOS[n.estado]?.label || n.estado, ...v]);
    return v;
  });

  if (filas.length) {
    // Totales; las horas base y el valor hora son por nómina: se promedian.
    const total = filas[0].map((_, i) => filas.reduce((s, f) => s + f[i], 0));
    total[2] = total[2] / filas.length;
    total[3] = total[3] / filas.length;
    const fila = ws.addRow(['TOTAL', '', '', '', '', ...total]);
    fila.font = { bold: true };
    fila.getCell(8).note = 'Promedio de las nóminas';
    fila.getCell(9).note = 'Promedio de las nóminas';
  }

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `nominas_por_periodo_${new Date().toISOString().slice(0, 10)}.xlsx`,
  );
};
